/**
 * A room's label, the one piece of text the floor plan view draws, the same
 * on both platforms in 2D, in 3D and in the exports: its name in the chosen
 * style, and its size just below when the view shows sizes, on a half-white
 * rounded backing so it reads over walls and floors alike. With a room
 * selected only that room is labeled, on a stronger backing.
 *
 * This module decides everything about a label but how it is drawn: which
 * rooms wear one, what its lines say, where it centers (`roomLabelCenter` in
 * 2D, `sceneRoomLabels` in 3D), how big its backing is, and where a press on
 * it lands (`roomLabelAt`). The topmost label, the last drawn, takes a press.
 * The two preferences behind it, room sizes and room names, are read back
 * here too.
 */
import { type FloorPlanRoom, roomPlacement, roomPlacementTransform } from '@kaiizen/kai-floor-plan';
import { formatLength, type InputKind, type MeasurementUnits, MIN_TOUCH_TARGET_PX, modelToScreen, type Viewport } from '../editor-model';
import type { LabelPlacement, SceneLabel } from '../view3d';
import { type AssemblerMutableState, displayRooms, levelRooms } from './assemblerState';
import type { AssemblyRoom } from './assemblyModel';

/**
 * How the plan names rooms: the host's default, its full name (group and
 * label, "Bedroom (Office)"), the group alone ("Bedroom"), the label alone
 * ("Office"), or not at all.
 */
export type RoomNameStyle = 'auto' | 'full' | 'group' | 'label' | 'none';

export const ROOM_NAME_STYLES: readonly { id: RoomNameStyle; label: string }[] = [
  { id: 'auto', label: 'Auto' },
  { id: 'full', label: 'Full name' },
  { id: 'group', label: 'Group only' },
  { id: 'label', label: 'Label only' },
  { id: 'none', label: 'None' },
];

export const isRoomNameStyle = (value: unknown): value is RoomNameStyle => ROOM_NAME_STYLES.some(({ id }) => id === value);

/** The view menus' room label rows: the sizes checkbox, and the heading over the `ROOM_NAME_STYLES` choices. */
export const ROOM_LABEL_MENU_LABELS = { sizes: 'Room sizes', names: 'Room names' } as const;

/** What the room labels say: each room's size under its name, and how rooms are named. */
export interface RoomLabelPreferences {
  /** Whether each room's size shows under its name. */
  roomSizes: boolean;
  /** How rooms are named. */
  roomNames: RoomNameStyle;
}

export const DEFAULT_ROOM_LABEL_PREFERENCES: RoomLabelPreferences = { roomSizes: false, roomNames: 'auto' };

/** The room sizes choice read back from a saved preference: anything but a yes or no takes the default. */
export const parseRoomSizes = (stored: unknown): boolean => typeof stored === 'boolean' ? stored : DEFAULT_ROOM_LABEL_PREFERENCES.roomSizes;

/** The room names choice read back from a saved preference: a style this version doesn't know takes the default. */
export const parseRoomNames = (stored: unknown): RoomNameStyle => isRoomNameStyle(stored) ? stored : DEFAULT_ROOM_LABEL_PREFERENCES.roomNames;

/** Everything a label's lines depend on beyond its room: the two preferences and the units its size reads in. */
export interface RoomLabelView extends RoomLabelPreferences {
  units: MeasurementUnits;
}

/** The room's name in a style, or its default name when the host gave none in that style; null when the style shows no name. */
export const assemblyRoomName = (room: AssemblyRoom, style: RoomNameStyle): string | null =>
  style === 'none' ? null : style === 'auto' ? room.name : room.names?.[style] ?? room.name;

/** The room's size as the plan shows it under its name, across by up and down the page: `12' 6½" × 15'`. */
export function roomSizeLabel(room: FloorPlanRoom, units: MeasurementUnits): string {
  const turned = Math.abs(Math.round(roomPlacement(room).rotation / 90)) % 2 === 1;
  const [across, down] = turned ? [room.length, room.width] : [room.width, room.length];
  return `${formatLength(across, units)} × ${formatLength(down, units)}`;
}

/** A room label's type wherever rooms are labeled, 2D and 3D, web and Kai Pro (px): its name in bold, its size under it a step smaller. */
export const ROOM_LABEL_FONT_PX = { name: 16, size: 14 } as const;

/** The label's backing around its lines (px): padding each side, and corner radius. */
export const ROOM_LABEL_BACKING = { paddingX: 6, paddingY: 2, radius: 5 } as const;

/** Each line's height, as a multiple of its font size. */
const LINE_HEIGHT = 1.3;
const BACKING_OPACITY = 0.5;
const SELECTED_BACKING_OPACITY = 0.8;
/** A CircularXX glyph's average advance, in ems: the width estimate where text can't be measured. */
const AVERAGE_GLYPH_EM = 0.58;
/** How far the selected room's label sits above its move gadget's center in 3D, clear of the gadget (px). */
const SELECTED_LABEL_LIFT_PX = 80;

/** One line of a label: the room's name, or its size. */
export interface RoomLabelLine {
  kind: 'name' | 'size';
  text: string;
  fontPx: number;
  bold: boolean;
  /** The line's height (px); the lines stack top to bottom. */
  height: number;
  /** Where the line centers vertically on screen (px). */
  y: number;
}

/** A box on screen (px). */
export interface RoomLabelBox {
  x: number;
  y: number;
  width: number;
  height: number;
}

/** A room's label, placed on screen. */
export interface PlacedRoomLabel {
  assemblyRoom: AssemblyRoom;
  view: RoomLabelView;
  /** Where the label centers (px). */
  center: { x: number; y: number };
  /** The selected room's id; null with no room selected. */
  selectedId: string | null;
}

/** How wide a line sets (px): a platform that can measure text passes its own measure. */
export type LabelLineWidth = (line: Pick<RoomLabelLine, 'text' | 'fontPx' | 'bold'>) => number;

/** A line's width estimated from its length, where text can't be measured. */
export const estimatedLineWidth: LabelLineWidth = ({ text, fontPx }) => text.length * fontPx * AVERAGE_GLYPH_EM;

export interface RoomLabelLayout {
  lines: RoomLabelLine[];
  /** The backing, which the lines center in. */
  box: RoomLabelBox;
  /** The backing's white: stronger on the selected room's label. */
  backingOpacity: number;
}

const selectedRoomId = (st: AssemblerMutableState): string | null => st.selection.kind === 'room' ? st.selection.id : null;

/** The label's lines and backing; null when the room wears no label: another room is selected, or the label would say nothing. */
export function roomLabelLayout(
  { assemblyRoom, view, center, selectedId }: PlacedRoomLabel,
  lineWidth: LabelLineWidth = estimatedLineWidth,
): RoomLabelLayout | null {
  if (selectedId !== null && assemblyRoom.id !== selectedId) return null;
  const name = assemblyRoomName(assemblyRoom, view.roomNames);
  const texts = [
    ...(name === null ? [] : [{ kind: 'name' as const, text: name, fontPx: ROOM_LABEL_FONT_PX.name, bold: true }]),
    ...(view.roomSizes ? [{ kind: 'size' as const, text: roomSizeLabel(assemblyRoom.room, view.units), fontPx: ROOM_LABEL_FONT_PX.size, bold: false }] : []),
  ];
  if (!texts.length) return null;
  const heights = texts.map(line => line.fontPx * LINE_HEIGHT);
  const height = heights.reduce((sum, lineHeight) => sum + lineHeight, 0);
  const top = center.y - height / 2;
  const width = Math.max(...texts.map(lineWidth));
  return {
    lines: texts.map((line, index) => ({
      ...line,
      height: heights[index],
      y: top + heights.slice(0, index).reduce((sum, lineHeight) => sum + lineHeight, 0) + heights[index] / 2,
    })),
    box: {
      x: center.x - width / 2 - ROOM_LABEL_BACKING.paddingX,
      y: top - ROOM_LABEL_BACKING.paddingY,
      width: width + 2 * ROOM_LABEL_BACKING.paddingX,
      height: height + 2 * ROOM_LABEL_BACKING.paddingY,
    },
    backingOpacity: assemblyRoom.id === selectedId ? SELECTED_BACKING_OPACITY : BACKING_OPACITY,
  };
}

export interface RoomLabelHitOptions {
  lineWidth?: LabelLineWidth;
  /** Mouse or finger: under touch the target measures at least a touch target each way about the label's center. Absent means a mouse. */
  inputKind?: InputKind;
}

/** Where a press selects the label's room: its backing, grown about the label's center to a touch target each way under touch. Null when the room wears no label. */
export function roomLabelHitBox(label: PlacedRoomLabel, { lineWidth, inputKind = 'pointer' }: RoomLabelHitOptions = {}): RoomLabelBox | null {
  const box = roomLabelLayout(label, lineWidth)?.box;
  if (!box) return null;
  const minimumSize = inputKind === 'touch' ? MIN_TOUCH_TARGET_PX : 0;
  const width = Math.max(box.width, minimumSize);
  const height = Math.max(box.height, minimumSize);
  return { x: box.x + (box.width - width) / 2, y: box.y + (box.height - height) / 2, width, height };
}

/** The label a press at (x, y) lands on: the topmost (the last drawn) whose target holds the point. */
export function roomLabelAt(labels: readonly PlacedRoomLabel[], x: number, y: number, options?: RoomLabelHitOptions): PlacedRoomLabel | undefined {
  return [...labels].reverse().find(label => {
    const box = roomLabelHitBox(label, options);
    return !!box && x >= box.x && x <= box.x + box.width && y >= box.y && y <= box.y + box.height;
  });
}

/** Where a room's label centers in the 2D plan: the room's placed center. */
export const roomLabelCenter = ({ room }: AssemblyRoom, vp: Viewport): { x: number; y: number } =>
  modelToScreen(vp, roomPlacementTransform(room)({ x: room.width / 2, y: room.length / 2 }));

/** The 2D plan's room labels: one per room on the level, in paint order, centered on its room. */
export const planRoomLabels = (st: AssemblerMutableState, view: RoomLabelView): PlacedRoomLabel[] =>
  levelRooms(st).map(assemblyRoom => ({ assemblyRoom, view, center: roomLabelCenter(assemblyRoom, st.viewport), selectedId: selectedRoomId(st) }));

/** A 3D frame's labels: the scene's labels, and where each one's anchor fell on screen (aligned by index). */
export interface SceneLabelFrame {
  labels: readonly SceneLabel[];
  placements: readonly LabelPlacement[];
}

/**
 * The room labels a 3D frame shows: one per scene label in front of the
 * camera, for the room its id names, centered where its anchor fell. Where
 * the view is editable, the selected room wears its move gadget, and its
 * label rises clear of it.
 */
export function sceneRoomLabels({ labels, placements }: SceneLabelFrame, st: AssemblerMutableState, view: RoomLabelView): PlacedRoomLabel[] {
  const roomsById = new Map(displayRooms(st).map(assemblyRoom => [assemblyRoom.id, assemblyRoom]));
  const selectedId = selectedRoomId(st);
  return labels.flatMap((label, index) => {
    const placement = placements[index];
    const assemblyRoom = label.id === undefined ? undefined : roomsById.get(label.id);
    if (!placement?.visible || !assemblyRoom) return [];
    const lift = !st.readOnly && assemblyRoom.id === selectedId ? SELECTED_LABEL_LIFT_PX : 0;
    return [{ assemblyRoom, view, center: { x: placement.x, y: placement.y - lift }, selectedId }];
  });
}
